<script setup lang="ts">
import { ref } from "vue";
import { UiText, UiIcon, UiTooltip } from "@shopexperts/shared";
import type { ExpertWalletTransaction } from "@shopexperts/legacy-api";
import { getToneAmountClasses } from "./walletTransactionDisplay";
import { useWalletTransaction } from "./useWalletTransaction";
import WalletTransactionRail from "./WalletTransactionRail.vue";
import WalletTransactionBreakdownList from "./WalletTransactionBreakdownList.vue";
import WalletTransactionEffect from "./WalletTransactionEffect.vue";

const props = withDefaults(
  defineProps<{
    transaction: ExpertWalletTransaction;
    isLast?: boolean;
  }>(),
  {
    isLast: false,
  },
);

const expanded = ref(false);

const {
  transaction,
  movement,
  tone,
  timelineState,
  headlineAmount,
  titleLabel,
  fullDate,
  dateTimeLabel,
  releasedByLabel,
  providerFeeLines,
  breakdownRows,
  hasDetails,
  effectTiles,
  references,
} = useWalletTransaction(() => props.transaction, {
  perspective: "self",
  resolveProjectRoute: (id) => ({ name: "expert-project-details", params: { id: String(id) } }),
});
</script>

<template>
  <li class="font-archivo relative flex gap-0 sm:gap-4">
    <WalletTransactionRail :state="timelineState" :is-last="isLast" />

    <article
      class="mb-3 min-w-0 flex-1 overflow-hidden rounded-xl border border-se-gray-200 bg-se-white transition-all duration-200 hover:border-se-gray-300 hover:shadow-[0_10px_28px_-22px_rgba(16,24,40,0.45)]"
    >
      <div class="flex flex-col gap-3 p-4">
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
          <div class="min-w-0">
            <div class="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
              <UiTooltip content="Transaction ID">
                <UiText
                  as="span"
                  variant="p-small"
                  :text="`#${transaction.id}`"
                  class="shrink-0 font-semibold text-se-gray-500"
                />
              </UiTooltip>
              <UiText as="p" variant="p" :text="titleLabel" class="min-w-0 font-semibold text-se-dark" />
            </div>

            <UiTooltip :content="fullDate">
              <UiText as="span" variant="p-small" :text="dateTimeLabel" class="text-se-gray-700" />
            </UiTooltip>
            <UiText
              v-if="releasedByLabel"
              as="span"
              variant="p-small"
              :text="releasedByLabel"
              class="ml-1.5 font-medium text-se-green before:mr-1.5 before:text-se-gray-500 before:content-['•']"
            />
          </div>

          <div class="flex shrink-0 flex-col items-end text-right">
            <div class="flex items-center justify-end gap-1.5" :class="getToneAmountClasses(tone)">
              <UiIcon v-if="headlineAmount.icon" :name="headlineAmount.icon" class="h-5 w-5" />
              <UiText as="span" variant="h4" :text="headlineAmount.label" class="font-semibold" />
            </div>
            <div class="flex items-center gap-0.5">
              <UiText as="span" variant="caption-normal" :text="movement.label" />
              <UiTooltip :content="movement.description">
                <UiIcon name="info-circle" class="h-2.5 w-2.5 text-se-gray-700" />
              </UiTooltip>
            </div>
          </div>
        </div>

        <div v-if="transaction.public_notes" class="rounded-md bg-se-gray-100 px-3 py-2">
          <UiText as="p" variant="small" :text="transaction.public_notes" class="text-se-dark" />
        </div>
      </div>

      <section v-if="hasDetails" class="border-t border-se-gray-200">
        <button
          type="button"
          class="group/toggle flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-se-gray-50"
          :aria-expanded="expanded"
          @click="expanded = !expanded"
        >
          <UiText as="h4" variant="p-small" text="Breakdown" class="font-semibold text-se-dark" />
          <span class="flex items-center gap-2">
            <UiText
              as="span"
              variant="small"
              :text="expanded ? 'Hide details' : 'View details'"
              class="text-se-gray-500 group-hover/toggle:text-se-dark"
            />
            <UiIcon
              name="chevron-down"
              class="h-4 w-4 text-se-gray-500 transition-transform duration-300 group-hover/toggle:text-se-dark"
              :class="expanded ? 'rotate-180' : ''"
            />
          </span>
        </button>

        <div
          class="grid transition-[grid-template-rows] duration-300 ease-out"
          :class="expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        >
          <div class="min-h-0 overflow-hidden">
            <div class="px-4 pb-4">
              <WalletTransactionBreakdownList
                :rows="breakdownRows"
                :provider-fee-lines="providerFeeLines"
                :currency="transaction.currency"
              />
            </div>
          </div>
        </div>
      </section>

      <WalletTransactionEffect
        heading="Effect on your wallet"
        :tiles="effectTiles"
        :references="references"
      />
    </article>
  </li>
</template>
